import { cn } from '@/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Spinner } from '../';

const buttonVariants = cva(
  `inline-flex items-center justify-center outline-0 border-0 whitespace-nowrap no-underline\
  select-none rounded-12 cursor-pointer align-middle text-[16px] text-primary font-medium\
  min-w-[64px] transition-colors duration-300 disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-gray-500\
  focus-visible:outline-2 focus-visible:outline focus-visible:outline-primary focus-visible:outline-offset-2`,
  {
    variants: {
      variant: {
        primary: 'bg-primary-500 text-black-300 hover:bg-primary-500/90',
        outline:
          'bg-transparent border border-solid border-black dark:border-white dark:text-white \
          hover:text-gray-70 dark:hover:text-white dark:hover:bg-white/5 disabled:border-gray-200 dark:disabled:border-gray-700',
        secondary:
          'bg-secondary-600 text-white hover:bg-secondary-600/90 disabled:bg-secondary-400',
        link: 'bg-transparent underline text-base rounded-0 min-w-0 dark:text-white',
        white: 'bg-white text-primary-600 hover:bg-gray-100',
        borderGradient:
          'bg-white/15 rounded-full relative text-white before:absolute before:content-[""] before:inset-0 before:rounded-full before:shadow-glow-inset disabled:!text-white disabled:opacity-50',
        borderGradientBlue:
          'bg-blue-600/15 rounded-full relative text-blue-600 before:absolute before:content-[""] before:inset-0 before:rounded-full before:shadow-glow-inset-blue hover:before:!shadow-none hover:-bottom-0.5 disabled:grayscale bottom-0 transition-all duration-150',
        blue: 'bg-secondary-600/10 text-secondary-600 hover:bg-secondary-600/20',
        'blue-600':
          'bg-blue-600 text-white disabled:grayscale disabled:!text-white',
        green: 'bg-green-600 bg-opacity-10 text-green-600',
        ghost:
          'bg-transparent hover:bg-gray-100 focus-visible:bg-gray-100 \
           dark:text-white dark:hover:text-black',
        destructive:
          'bg-error-600 text-white transition-all duration-300 hover:shadow-[inset_0_0_10px_0] hover:shadow-black/50 disabled:bg-red-300 disabled:text-white',
        darkBlue:
          'bg-primary-600 text-white hover:bg-primary-500 disabled:bg-red-300 disabled:text-white',
        gray: 'bg-gray-100 text-primary-600 ',
        goldGradient:
          'bg-gradient-to-b from-gold-200 to-gold-600  text-white disabled:bg-red-300 disabled:text-white',
        lightPrimary:
          'bg-yellow-600/[0.12] text-gold-600 disabled:bg-red-300 disabled:text-white',
      },
      size: {
        sm: 'h-[32px] py-2 px-3 text-xs',
        md: 'h-10 py-[11px] px-3',
        lg: 'h-12 px-8 py-[15px]',
      },
      isIcon: {
        true: 'rounded-full h-auto min-w-0',
      },
      loading: {
        true: 'relative',
      },
    },
    compoundVariants: [
      {
        variant: ['primary', 'secondary'],
        class: 'disabled:bg-gray-400 disabled:text-white',
      },
      { variant: 'link', class: 'p-[1px]' },
      { variant: 'link', size: 'sm', class: 'text-sm' },
      { variant: 'ghost', size: 'sm', class: 'px-3' },
      { variant: 'ghost', size: 'md', class: 'px-4' },
      { variant: 'ghost', size: 'md', class: 'px-6' },
      { isIcon: true, size: 'sm', class: 'p-[7px] size-[34px]' },
      { isIcon: true, size: 'md', class: 'p-2.5 size-2.5' },
      { isIcon: true, size: 'lg', class: 'p-3 size-12' },
      {
        loading: true,
        class: 'disabled:text-transparent',
      },
    ],
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

type AnchorProps = Pick<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'target'
>;
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    AnchorProps {
  as?: React.ElementType;
  loading?: boolean;
  isIcon?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant: buttonVariant,
      size,
      children,
      disabled,
      isIcon,
      startIcon,
      endIcon,
      href,
      as,
      loading = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = as || (href ? 'a' : 'button');
    const variant = isIcon && !buttonVariant ? 'outline' : buttonVariant;
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            isIcon,
            loading,
            className,
          }),
          'outline-none'
        )}
        href={href}
        ref={ref}
        disabled={isDisabled}
        data-disabled={isDisabled}
        type={Comp === 'button' ? type : undefined}
        {...props}
      >
        {loading && (
          <Spinner
            className={cn(
              'text-white/30 absolute left-1/2 transform -translate-x-1/2',
              (variant === 'outline' || variant === 'ghost') &&
                'dark:text-white/30'
            )}
          />
        )}
        {startIcon && !isIcon && (
          <span className="[display:inherit] -ml-4 mr-8">{startIcon}</span>
        )}
        {children}
        {endIcon && !isIcon && (
          <span className="[display:inherit] -mr-4 ml-8">{endIcon}</span>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
